import React, { useState, useEffect, useCallback } from 'react';

const CATEGORY_OPTIONS = [
    { value: 'mattress', label: 'Mattress' },
    { value: 'supplement', label: 'Supplement' },
    { value: 'wearable', label: 'Wearable' },
    { value: 'app', label: 'App' },
    { value: 'accessory', label: 'Accessory' },
];

const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'expired', label: 'Expired' },
];

const AffiliateManager = () => {
    const [affiliates, setAffiliates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [view, setView] = useState('list'); // list | add | edit
    const [editingId, setEditingId] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');

    // URL input state
    const [affiliateUrl, setAffiliateUrl] = useState('');
    const [productUrl, setProductUrl] = useState('');

    // Form state for the parsed/editable affiliate
    const [form, setForm] = useState({
        product_name: '',
        merchant: '',
        affiliate_url: '',
        category: 'accessory',
        description: '',
        commission_rate: 0,
        keywords: [],
        status: 'active',
        expires_at: '',
    });
    const [keywordInput, setKeywordInput] = useState('');
    const [confidence, setConfidence] = useState(null);

    const clearMessages = () => {
        setError(null);
        setSuccess(null);
    };

    const fetchAffiliates = useCallback(async () => {
        try {
            const response = await fetch('/.netlify/functions/parse-affiliate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'list' }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to fetch');
            setAffiliates(data.affiliates || []);
        } catch (err) {
            setError('Failed to load affiliates: ' + err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAffiliates();
    }, [fetchAffiliates]);

    const resetForm = () => {
        setForm({
            product_name: '',
            merchant: '',
            affiliate_url: '',
            category: 'accessory',
            description: '',
            commission_rate: 0,
            keywords: [],
            status: 'active',
            expires_at: '',
        });
        setAffiliateUrl('');
        setProductUrl('');
        setKeywordInput('');
        setConfidence(null);
        setEditingId(null);
    };

    const handleParse = async () => {
        if (!affiliateUrl.trim()) {
            setError('Paste an affiliate URL first');
            return;
        }
        clearMessages();
        setParsing(true);

        try {
            const response = await fetch('/.netlify/functions/parse-affiliate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    affiliateUrl: affiliateUrl.trim(),
                    productUrl: productUrl.trim() || undefined,
                }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Parse failed');

            const parsed = data.parsed;
            setForm({
                product_name: parsed.product_name,
                merchant: parsed.merchant,
                affiliate_url: parsed.affiliate_url,
                category: parsed.category,
                description: parsed.description,
                commission_rate: parsed.commission_rate,
                keywords: parsed.keywords || [],
                status: 'active',
                expires_at: '',
            });
            setConfidence(parsed.confidence);
            setSuccess(`AI parsed successfully (confidence: ${parsed.confidence}). Review and save below.`);
        } catch (err) {
            setError('Parse failed: ' + err.message);
        } finally {
            setParsing(false);
        }
    };

    const handleSave = async () => {
        if (!form.product_name || !form.merchant || !form.affiliate_url || !form.category) {
            setError('Product name, merchant, affiliate URL, and category are required');
            return;
        }
        clearMessages();
        setSaving(true);

        try {
            const payload = editingId
                ? { action: 'update', id: editingId, affiliateData: form }
                : { action: 'save', affiliateData: form };

            const response = await fetch('/.netlify/functions/parse-affiliate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Save failed');

            setSuccess(editingId ? 'Affiliate updated!' : 'Affiliate saved!');
            resetForm();
            setView('list');
            await fetchAffiliates();
        } catch (err) {
            setError('Save failed: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
        clearMessages();

        try {
            const response = await fetch('/.netlify/functions/parse-affiliate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'delete', id }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Delete failed');

            setSuccess('Affiliate deleted');
            await fetchAffiliates();
        } catch (err) {
            setError('Delete failed: ' + err.message);
        }
    };

    const handleEdit = (affiliate) => {
        setForm({
            product_name: affiliate.product_name,
            merchant: affiliate.merchant,
            affiliate_url: affiliate.affiliate_url,
            category: affiliate.category,
            description: affiliate.description || '',
            commission_rate: affiliate.commission_rate || 0,
            keywords: affiliate.keywords || [],
            status: affiliate.status || 'active',
            expires_at: affiliate.expires_at ? affiliate.expires_at.split('T')[0] : '',
        });
        setEditingId(affiliate.id);
        setConfidence(null);
        setView('edit');
    };

    const handleAddKeyword = () => {
        const kw = keywordInput.trim().toLowerCase();
        if (kw && !form.keywords.includes(kw)) {
            setForm(prev => ({ ...prev, keywords: [...prev.keywords, kw] }));
        }
        setKeywordInput('');
    };

    const handleRemoveKeyword = (keyword) => {
        setForm(prev => ({ ...prev, keywords: prev.keywords.filter(k => k !== keyword) }));
    };

    const handleKeywordKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddKeyword();
        }
    };

    const filteredAffiliates = affiliates.filter(a => {
        if (filterCategory !== 'all' && a.category !== filterCategory) return false;
        if (filterStatus !== 'all' && a.status !== filterStatus) return false;
        return true;
    });

    // Render the URL input + parse section
    const renderParseSection = () => (
        <div className="ai-generation-panel">
            <h3>AI Auto-Fill</h3>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                Paste the affiliate URL (and optionally the product page URL) and AI will extract all the details.
            </p>
            <div className="affiliate-parse-inputs">
                <div className="form-group">
                    <label>Affiliate URL *</label>
                    <input
                        type="url"
                        placeholder="https://impact.com/affiliate/your-link..."
                        value={affiliateUrl}
                        onChange={(e) => setAffiliateUrl(e.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label>Product URL (optional)</label>
                    <input
                        type="url"
                        placeholder="https://brand.com/product-page..."
                        value={productUrl}
                        onChange={(e) => setProductUrl(e.target.value)}
                    />
                </div>
            </div>
            {parsing ? (
                <div className="generating-status">
                    <div className="spinner"></div>
                    AI is parsing the URLs...
                </div>
            ) : (
                <button className="btn-primary" onClick={handleParse} style={{ marginTop: '1rem' }}>
                    Parse with AI
                </button>
            )}
        </div>
    );

    // Render the editable form
    const renderForm = () => (
        <div className="affiliate-form">
            {confidence && (
                <div className={`affiliate-confidence confidence-${confidence}`}>
                    AI Confidence: <strong>{confidence}</strong>
                    {confidence === 'low' && ' — double-check these details'}
                    {confidence === 'medium' && ' — looks reasonable, verify key fields'}
                    {confidence === 'high' && ' — looks good!'}
                </div>
            )}

            <div className="form-row">
                <div className="form-group">
                    <label>Product Name *</label>
                    <input
                        type="text"
                        value={form.product_name}
                        onChange={(e) => setForm(prev => ({ ...prev, product_name: e.target.value }))}
                        placeholder="e.g. Dreamie Weighted Blanket"
                    />
                </div>
                <div className="form-group">
                    <label>Merchant *</label>
                    <input
                        type="text"
                        value={form.merchant}
                        onChange={(e) => setForm(prev => ({ ...prev, merchant: e.target.value }))}
                        placeholder="e.g. Dreamie"
                    />
                </div>
            </div>

            <div className="form-group">
                <label>Affiliate URL *</label>
                <input
                    type="url"
                    value={form.affiliate_url}
                    onChange={(e) => setForm(prev => ({ ...prev, affiliate_url: e.target.value }))}
                    placeholder="https://..."
                />
            </div>

            <div className="form-group">
                <label>Description</label>
                <textarea
                    value={form.description}
                    onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief product description for article integration..."
                    style={{ minHeight: '100px' }}
                />
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label>Category *</label>
                    <select
                        value={form.category}
                        onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                    >
                        {CATEGORY_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label>Commission Rate (%)</label>
                    <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={form.commission_rate}
                        onChange={(e) => setForm(prev => ({ ...prev, commission_rate: parseFloat(e.target.value) || 0 }))}
                    />
                </div>
                <div className="form-group">
                    <label>Status</label>
                    <select
                        value={form.status}
                        onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
                    >
                        {STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="form-group">
                <label>Expires At (optional)</label>
                <input
                    type="date"
                    value={form.expires_at}
                    onChange={(e) => setForm(prev => ({ ...prev, expires_at: e.target.value }))}
                />
            </div>

            <div className="form-group">
                <label>Keywords</label>
                <div className="affiliate-keywords-input">
                    <input
                        type="text"
                        value={keywordInput}
                        onChange={(e) => setKeywordInput(e.target.value)}
                        onKeyDown={handleKeywordKeyDown}
                        placeholder="Type keyword and press Enter..."
                    />
                    <button className="btn-secondary" onClick={handleAddKeyword} type="button">Add</button>
                </div>
                {form.keywords.length > 0 && (
                    <div className="affiliate-keywords-list">
                        {form.keywords.map((kw) => (
                            <span key={kw} className="affiliate-keyword-tag">
                                {kw}
                                <button onClick={() => handleRemoveKeyword(kw)} title="Remove">&times;</button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="editor-actions">
                {saving ? (
                    <div className="generating-status">
                        <div className="spinner"></div>
                        Saving...
                    </div>
                ) : (
                    <>
                        <button className="btn-primary" onClick={handleSave}>
                            {editingId ? 'Update Affiliate' : 'Save Affiliate'}
                        </button>
                        <button className="btn-secondary" onClick={() => { resetForm(); setView('list'); }}>
                            Cancel
                        </button>
                    </>
                )}
            </div>
        </div>
    );

    // Render the affiliates list
    const renderList = () => (
        <>
            <div className="articles-list-header">
                <h2>Affiliates ({filteredAffiliates.length})</h2>
                <button className="btn-primary" onClick={() => { resetForm(); setView('add'); }}>
                    + Add Affiliate
                </button>
            </div>

            <div className="affiliate-filters">
                <select
                    className="keywords-sort"
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                >
                    <option value="all">All Categories</option>
                    {CATEGORY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <select
                    className="keywords-sort"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                >
                    <option value="all">All Statuses</option>
                    {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {filteredAffiliates.length === 0 ? (
                <div className="empty-state">
                    <p>{affiliates.length === 0 ? 'No affiliates yet. Add your first one!' : 'No affiliates match your filters.'}</p>
                </div>
            ) : (
                <div className="affiliate-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Merchant</th>
                                <th>Category</th>
                                <th>Commission</th>
                                <th>Keywords</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAffiliates.map((aff) => (
                                <tr key={aff.id}>
                                    <td>
                                        <div className="affiliate-product-name">{aff.product_name}</div>
                                        <div className="affiliate-url-preview" title={aff.affiliate_url}>
                                            {aff.affiliate_url.substring(0, 40)}...
                                        </div>
                                    </td>
                                    <td>{aff.merchant}</td>
                                    <td><span className="badge badge-featured">{aff.category}</span></td>
                                    <td>{aff.commission_rate}%</td>
                                    <td>
                                        <div className="affiliate-keywords-compact">
                                            {(aff.keywords || []).slice(0, 3).map(kw => (
                                                <span key={kw} className="affiliate-keyword-mini">{kw}</span>
                                            ))}
                                            {(aff.keywords || []).length > 3 && (
                                                <span className="affiliate-keyword-mini more">
                                                    +{aff.keywords.length - 3}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`badge badge-${aff.status === 'active' ? 'active' : 'draft'}`}>
                                            {aff.status}
                                        </span>
                                    </td>
                                    <td className="actions">
                                        <button className="btn-secondary" onClick={() => handleEdit(aff)}>
                                            Edit
                                        </button>
                                        <button className="btn-danger" onClick={() => handleDelete(aff.id, aff.product_name)}>
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );

    if (loading) {
        return (
            <div className="keywords-generator">
                <div className="generating-status">
                    <div className="spinner"></div>
                    Loading affiliates...
                </div>
            </div>
        );
    }

    return (
        <div className="keywords-generator">
            {error && <div className="admin-status error">{error}</div>}
            {success && <div className="admin-status success">{success}</div>}

            {view === 'list' && renderList()}

            {view === 'add' && (
                <>
                    <div className="articles-list-header">
                        <h2>Add New Affiliate</h2>
                        <button className="btn-secondary" onClick={() => { resetForm(); setView('list'); }}>
                            Back to List
                        </button>
                    </div>
                    {renderParseSection()}
                    <div style={{ marginTop: '2rem' }}>
                        <h3 style={{ fontFamily: "'Playfair Display', serif", marginBottom: '1rem' }}>
                            Affiliate Details
                        </h3>
                        {renderForm()}
                    </div>
                </>
            )}

            {view === 'edit' && (
                <>
                    <div className="articles-list-header">
                        <h2>Edit Affiliate</h2>
                        <button className="btn-secondary" onClick={() => { resetForm(); setView('list'); }}>
                            Back to List
                        </button>
                    </div>
                    {renderForm()}
                </>
            )}
        </div>
    );
};

export default AffiliateManager;
